import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";

import { useApi } from "../../utils/api";
import { ENDPOINTS } from "../../config/endpoints";

const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const getFileIcon = (file) => {
  const ext = (file.orijinal_ad || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <PictureAsPdfIcon sx={{ color: "#EF4444", fontSize: 20 }} />;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
    return <ImageIcon sx={{ color: "#10B981", fontSize: 20 }} />;
  return <InsertDriveFileIcon sx={{ fontSize: 20, color: "#6B7280" }} />;
};

const ENGEL_RAPORU_KOD = "engel_raporu";

const CHIP_COLORS = ["primary", "secondary", "success", "info", "warning", "error", "default"];
const colorForKod = (kod) => {
  if (!kod) return "default";
  let h = 0;
  for (let i = 0; i < kod.length; i++) h = (h * 31 + kod.charCodeAt(i)) >>> 0;
  return CHIP_COLORS[h % CHIP_COLORS.length];
};

const EmployeeFilesTab = ({ personelId, employee }) => {
  const http = useApi();

  const [dosyalar, setDosyalar] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [dosyaLoading, setDosyaLoading] = useState(false);
  const [dosyaError, setDosyaError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const docTypeByKod = (kod) => docTypes.find((t) => t.kod === kod);
  const getDocLabel = (kod) => docTypeByKod(kod)?.ad || kod || "-";

  const fetchDosyalar = async () => {
    if (!personelId) return;
    setDosyaLoading(true);
    setDosyaError("");
    try {
      const data = await http.get(ENDPOINTS.PERSONEL.FILES(personelId));
      setDosyalar(Array.isArray(data) ? data : []);
    } catch (err) {
      setDosyaError(err.message || "Dosyalar alınamadı.");
    } finally {
      setDosyaLoading(false);
    }
  };

  const fetchDocTypes = async () => {
    try {
      const data = await http.get(ENDPOINTS.DOSYA_TURU.LIST, { only_aktif: true });
      setDocTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      // sessizce geç — sadece label fallback olur (kod görünür)
    }
  };

  useEffect(() => {
    fetchDosyalar();
  }, [personelId]);

  useEffect(() => {
    fetchDocTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!selectedType) {
      setDosyaError("Lütfen önce dosya türünü seçiniz.");
      return;
    }

    setUploading(true);
    setDosyaError("");

    const fd = new FormData();
    fd.append("tur", selectedType);
    Array.from(files).forEach((f) => fd.append("files", f));

    try {
      await http.postForm(ENDPOINTS.PERSONEL.FILES(personelId), fd);
      fetchDosyalar();
    } catch (err) {
      setDosyaError(err.message || "Dosya yüklenemedi.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files.length) onUploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDownload = async (file) => {
    try {
      const blob = await http.getBlob(
        ENDPOINTS.PERSONEL.FILE_DOWNLOAD(personelId, file.id)
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.orijinal_ad || "dosya";
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDosyaError("İndirme başarısız.");
    }
  };

  const onDeleteFile = (file) => {
    setDeleteTarget(file);
  };

  const handleConfirmDelete = async () => {
    const file = deleteTarget;
    setDeleteTarget(null);
    setDosyalar((prev) => prev.filter((f) => f.id !== file.id));
    try {
      await http.del(ENDPOINTS.PERSONEL.FILE_DETAIL(personelId, file.id));
    } catch (err) {
      setDosyaError(err.message || "Silme başarısız.");
      fetchDosyalar();
    }
  };

  const existingTypes = new Set(dosyalar.map((d) => d.tur));
  const isEngelli = !!employee?.engelli_mi;

  const requiredDocsForEmployee = docTypes.filter((doc) => {
    if (!doc.zorunlu) return false;
    if (doc.kod === ENGEL_RAPORU_KOD) return isEngelli;
    return true;
  });

  const missingDocs = requiredDocsForEmployee.filter(
    (doc) => !existingTypes.has(doc.kod)
  );

  const displayedFiles = selectedType
    ? dosyalar.filter((d) => d.tur === selectedType)
    : dosyalar;

  return (
    <Box>
      {/* Üst bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            İnsan Kaynakları Dosyaları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam: {dosyalar.length} dosya
            {selectedType && <> • Görüntülenen: {displayedFiles.length} dosya</>}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" color="text.secondary">
            Zorunlu dosya sayısı: {requiredDocsForEmployee.length}
          </Typography>
          {missingDocs.length > 0 ? (
            <Typography variant="body2" color="error">
              Eksik: {missingDocs.length} dosya
            </Typography>
          ) : (
            <Typography variant="body2" color="success.main">
              Tüm zorunlu dosyalar yüklendi
            </Typography>
          )}
        </Box>
      </Box>

      {/* Eksik dosya uyarısı */}
      {missingDocs.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Eksik dosyalar: {missingDocs.map((d) => d.ad).join(", ")}
        </Alert>
      )}

      {/* Dosya türü seçimi */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Dosya Türü Seç</InputLabel>
        <Select
          value={selectedType}
          label="Dosya Türü Seç"
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <MenuItem value=""><em>Hepsi</em></MenuItem>
          {docTypes.map((d) => (
            <MenuItem key={d.kod} value={d.kod}>
              {d.ad}
              {d.kod === ENGEL_RAPORU_KOD ? " (Engelli için)" : ""}
              {!d.zorunlu && d.kod !== ENGEL_RAPORU_KOD ? " (Opsiyonel)" : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Upload alanı */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          mb: 3,
          bgcolor: dragActive ? "#eff6ff" : "#f9fafb",
          borderColor: dragActive ? "#3b82f6" : "#d1d5db",
          transition: "all 0.2s",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 32, color: "#3b82f6" }} />
        <Box>
          <Typography variant="body1" fontWeight={500}>
            Dosyaları buraya sürükleyin veya{" "}
            <Box component="span" sx={{ color: "#3b82f6", textDecoration: "underline" }}>
              göz atın
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Önce dosya türünü seçmeyi unutmayın.
          </Typography>
        </Box>

        {uploading && <CircularProgress size={20} />}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => onUploadFiles(e.target.files)}
        />
      </Box>

      {dosyaError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {dosyaError}
        </Alert>
      )}

      {/* Dosya listesi — tablo */}
      {dosyaLoading ? (
        <LinearProgress />
      ) : displayedFiles.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          {selectedType
            ? "Bu dosya türüne ait kayıt bulunmuyor."
            : "Kayıtlı dosya bulunmuyor."}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "gray", width: 40 }} />
                <TableCell sx={{ fontWeight: 600, color: "gray" }}>Dosya Adı</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "gray" }}>Tür</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "gray" }}>Boyut</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "gray" }} align="right">
                  İşlemler
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedFiles.map((file, idx) => (
                <TableRow
                  key={file.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell align="center">{getFileIcon(file)}</TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 260 }}>
                      {file.orijinal_ad}
                    </Typography>
                    {file.aciklama && (
                      <Typography variant="caption" color="text.secondary">
                        {file.aciklama}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={getDocLabel(file.tur)}
                      color={colorForKod(file.tur)}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(file.boyut)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="İndir">
                      <IconButton size="small" onClick={() => onDownload(file)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton size="small" color="error" onClick={() => onDeleteFile(file)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Silme Onay Dialogu */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Dosyayı Sil</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
            {deleteTarget && getFileIcon(deleteTarget)}
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {deleteTarget?.orijinal_ad}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bu dosya kalıcı olarak silinecek. Bu işlem geri alınamaz.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeFilesTab;